'use client'

import { useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/nextjs';
import { initSentry } from '@/instrumentation-client';

// Type declaration for Sentry global
declare global {
  interface Window {
    __SENTRY__?: {
      hub?: unknown;
    };
  }
}

export default function ConditionalAnalytics() {
  const { hasConsent } = useCookieConsent();

  useEffect(() => {
    // Only initialize PostHog if analytics consent is given
    if (hasConsent('analytics') && typeof window !== 'undefined') {
      if (!posthog.__loaded) {
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY!, {
          api_host: '/ingest',
          ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
          person_profiles: 'always',
          capture_pageview: false, // Disable automatic pageview capture, as we capture manually

          autocapture: {
            dom_event_allowlist: ['click', 'submit', 'change']
          },

          // Enable session recording with privacy protection
          session_recording: {
            maskAllInputs: true,
            maskInputOptions: {
              password: true,
              email: false // Allow email since it's a personal app
            },
          },

          // Explicitly enable session recording
          disable_session_recording: false,

          // Disable debug mode in production
          debug: process.env.NODE_ENV === 'development'
        });

        console.log('PostHog initialized with consent');
      }
    } else if (typeof window !== 'undefined' && posthog.__loaded) {
      // If consent is withdrawn, reset PostHog
      posthog.reset();
      console.log('PostHog reset due to withdrawn consent');
    }

    // Only initialize Sentry if error monitoring consent is given
    if (hasConsent('errorMonitoring') && typeof window !== 'undefined') {
      if (!window.__SENTRY__?.hub) {
        initSentry();
        console.log('Sentry initialized with consent');
      }
    } else if (typeof window !== 'undefined' && window.__SENTRY__?.hub) {
      // If consent is withdrawn, close Sentry
      Sentry.close();
      console.log('Sentry closed due to withdrawn consent');
    }
  }, [hasConsent]);

  // Only render Vercel Analytics if analytics consent is given
  if (!hasConsent('analytics')) {
    return null;
  }

  return <Analytics />;
}